import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import "chartjs-adapter-date-fns"
import { subscribeToMarketPrices } from "../channels/market_prices_channel"

Chart.register(...registerables)

const PERIOD_HOURS = { "6h": 6, "1d": 24, "1w": 24 * 7, "1m": 24 * 30, "all": null }
const PERIOD_UNIT  = { "6h": "minute", "1d": "minute", "1w": "hour", "1m": "hour", "all": "hour" }
const MAX_DATA_POINTS = 5000

export default class extends Controller {
  static values = { url: String, marketId: String }
  static targets = ["canvas", "periodBtn"]

  async connect() {
    const response = await fetch(this.urlValue, { headers: { "Accept": "application/json" } })
    if (!response.ok) return
    this.allData = await response.json()
    this._currentPeriod = "all"
    this.applyPeriod("all")

    if (this.hasMarketIdValue) {
      this.priceSubscription = subscribeToMarketPrices(this.marketIdValue, {
        received: (data) => this._appendLiveDataPoint(data)
      })
    }
  }

  setPeriod(event) {
    this.applyPeriod(event.currentTarget.dataset.period)
  }

  applyPeriod(period) {
    this._currentPeriod = period
    const filtered = this.filterData(period)
    const unit = PERIOD_UNIT[period] || "hour"
    this.highlightButton(period)
    if (this.chart) {
      this.chart.data.labels = filtered.labels
      this.chart.data.datasets[0].data = filtered.yes_prices
      this.chart.options.scales.x.time.unit = unit
      this.chart.update()
    } else {
      this.renderChart(filtered, unit)
    }
  }

  filterData(period) {
    const hours = PERIOD_HOURS[period]
    if (!hours) {
      return {
        labels:     this.allData.labels,
        yes_prices: this.allData.yes_prices
      }
    }
    const cutoff = Date.now() - hours * 60 * 60 * 1000
    const indices = this.allData.labels
      .map((t, i) => ({ t: new Date(t).getTime(), i }))
      .filter(({ t }) => t >= cutoff)
      .map(({ i }) => i)
    return {
      labels:     indices.map(i => this.allData.labels[i]),
      yes_prices: indices.map(i => this.allData.yes_prices[i])
    }
  }

  highlightButton(period) {
    this.periodBtnTargets.forEach(btn => {
      const active = btn.dataset.period === period
      btn.classList.toggle("bg-slate-700", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-slate-400", !active)
    })
  }

  renderChart(data, unit) {
    const ctx = this.canvasTarget.getContext("2d")
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "Sí",
            data: data.yes_prices,
            borderColor: "#4ade80",
            backgroundColor: "rgba(74, 222, 128, 0.1)",
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointRadius: 0,
            pointHitRadius: 10
          },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: {
            labels: { color: "#94a3b8", usePointStyle: true, pointStyle: "circle" }
          },
          tooltip: {
            backgroundColor: "#1e293b",
            borderColor: "#475569",
            borderWidth: 1,
            titleColor: "#f1f5f9",
            bodyColor: "#cbd5e1",
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`
            }
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit,
              displayFormats: {
                minute: "dd MMM HH:mm",
                hour:   "dd MMM HH:mm"
              }
            },
            grid: { color: "rgba(51, 65, 85, 0.3)" },
            ticks: { color: "#94a3b8", maxTicksLimit: 8 }
          },
          y: {
            min: 0,
            max: 1,
            grid: { color: "rgba(51, 65, 85, 0.3)" },
            ticks: {
              color: "#94a3b8",
              callback: (v) => `${(v * 100).toFixed(0)}%`
            }
          }
        }
      }
    })
  }

  _appendLiveDataPoint(data) {
    if (!this.allData) return

    const timestamp = data.timestamp || new Date().toISOString()
    this.allData.labels.push(timestamp)
    this.allData.yes_prices.push(data.probability_yes)

    // Trim to prevent memory growth in long sessions
    if (this.allData.labels.length > MAX_DATA_POINTS) {
      this.allData.labels.splice(0, 1)
      this.allData.yes_prices.splice(0, 1)
    }

    this.applyPeriod(this._currentPeriod || "all")
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
    if (this.priceSubscription) {
      this.priceSubscription.unsubscribe()
      this.priceSubscription = null
    }
  }
}
